import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useSession } from "../auth/SessionProvider";
import { OnboardingPage } from "../onboarding/OnboardingPage";
import {
  useAccountSettings,
  useEnsurePersonalProfile,
  useEntitlement,
  useModules,
  useProfiles,
} from "./api/queries";
import type { AccountSettings, Profile, ProfileModule } from "./api/platform-api";
import { normalizeAccess, type PlatformAccess, type ProfileModuleId } from "./model/access";

interface AccountContextValue {
  settings: AccountSettings;
  access: PlatformAccess;
  profiles: Profile[];
  visibleProfiles: Profile[];
  modules: ProfileModule[];
  activeProfileId: string | null;
  setActiveProfileId(profileId: string | null): void;
  enabledModules(profileId: string): Set<ProfileModuleId>;
}

const AccountContext = createContext<AccountContextValue | null>(null);

export function AccountProvider({ children }: { children: ReactNode }) {
  const { user } = useSession();
  if (user === null) throw new Error("Conta requer sessão autenticada");
  const settingsQuery = useAccountSettings(user.id);
  const entitlementQuery = useEntitlement(user.id);
  const profilesQuery = useProfiles(user.id);
  const profileIds = useMemo(
    () => profilesQuery.data?.map((profile) => profile.id) ?? [],
    [profilesQuery.data],
  );
  const modulesQuery = useModules(user.id, profileIds);
  const ensurePersonal = useEnsurePersonalProfile(user.id);
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);

  const settings = settingsQuery.data;
  const profiles = useMemo(() => profilesQuery.data ?? [], [profilesQuery.data]);

  useEffect(() => {
    if (
      settings?.usage_mode === "personal" &&
      settings.onboarding_completed_at !== null &&
      profilesQuery.isSuccess &&
      !profiles.some((profile) => profile.tipo_perfil === "pessoal") &&
      !ensurePersonal.isPending
    ) {
      const fallbackName = String(
        user.user_metadata.full_name ?? user.user_metadata.name ?? "Meu perfil",
      );
      ensurePersonal.mutate(fallbackName);
    }
  }, [settings, profiles, profilesQuery.isSuccess, ensurePersonal, user.user_metadata]);

  if (settingsQuery.isPending || profilesQuery.isPending || entitlementQuery.isPending) {
    return (
      <main className="center-state" aria-busy="true">
        Carregando sua conta…
      </main>
    );
  }
  if (settingsQuery.isError || profilesQuery.isError || entitlementQuery.isError) {
    return (
      <main className="center-state error-state" role="alert">
        Não foi possível carregar sua conta. Atualize a página para tentar novamente.
      </main>
    );
  }
  if (settings == null || settings.onboarding_completed_at === null) {
    return <OnboardingPage />;
  }
  if (modulesQuery.isPending && profiles.length > 0) {
    return (
      <main className="center-state" aria-busy="true">
        Carregando módulos…
      </main>
    );
  }
  if (modulesQuery.isError || ensurePersonal.isError) {
    return (
      <main className="center-state error-state" role="alert">
        Não foi possível carregar Meu perfil e seus módulos.
      </main>
    );
  }

  const usageMode = settings.usage_mode === "professional" ? "professional" : "personal";
  const professionalProfile =
    settings.professional_profile === "coach" ||
    settings.professional_profile === "nutritionist" ||
    settings.professional_profile === "trainer" ||
    settings.professional_profile === "other"
      ? settings.professional_profile
      : null;
  const access = normalizeAccess(usageMode, professionalProfile, entitlementQuery.data ?? null);
  const visibleProfiles =
    usageMode === "personal"
      ? profiles.filter((profile) => profile.tipo_perfil === "pessoal")
      : profiles;
  const modules = modulesQuery.data ?? [];
  const value: AccountContextValue = {
    settings,
    access,
    profiles,
    visibleProfiles,
    modules,
    activeProfileId,
    setActiveProfileId,
    enabledModules(profileId) {
      const enabled = new Set<ProfileModuleId>(["assistente"]);
      for (const row of modules) {
        if (row.aluno_id === profileId && row.habilitado)
          enabled.add(row.modulo as ProfileModuleId);
      }
      return enabled;
    },
  };
  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount(): AccountContextValue {
  const value = useContext(AccountContext);
  if (value === null) throw new Error("AccountProvider ausente");
  return value;
}
